/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $insertNodeToNearestRoot, mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  LexicalCommand,
  LexicalEditor,
} from "lexical";
import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { CAN_USE_DOM } from "../../utils/canUseDOM";

import { $createImageNode, $isImageNode, SlidImageNode, ImagePayload } from "../../nodes/SlidImageNode/ImageNode";
import { DialogActions, DialogButtonsList } from "../../ui/Dialog";
import TextInput from "../../ui/TextInput";
import useEditorStore from "store/useEditorStore";
import { Button } from "@slid/slid-ips";
import { useTranslation } from "react-i18next";
import ImageFileInput from "components/NewEditor/ui/ImageFileInput";
import { useImageNodeStore } from "components/NewEditor/store/useImageNodeStore";
import { uploadBase64Image } from "utils/aws/s3Interface";
import * as Sentry from "@sentry/react";
import { useAppDispatch } from "hooks";
import { registerClip } from "redux/actions/vdocsActions";
import { convertImageToBase64WithCanvas } from "utils/utils";
import { $createSlidParagraphNode } from "components/NewEditor/nodes/SlidParagraphNode";

export type InsertImagePayload = Readonly<ImagePayload>;

const getDOMSelection = (targetWindow: Window | null): Selection | null => (CAN_USE_DOM ? (targetWindow || window).getSelection() : null);

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand("INSERT_IMAGE_COMMAND");
export const FOCUS_IMAGE_DUMMY_TEXTAREA_COMMAND: LexicalCommand<void> = createCommand("FOCUS_IMAGE_DUMMY_TEXTAREA_COMMAND");

export function InsertImageUriDialogBody({ onClick }: { onClick: (payload: InsertImagePayload) => void }) {
  const [src, setSrc] = useState("");
  const { t } = useTranslation("EditorModal");
  const isDisabled = src === "";
  const { currentDocumentKey } = useEditorStore();
  const dispatch = useAppDispatch();

  return (
    <>
      <TextInput label="URL" placeholder={t("image.placeholder", { keySeparator: ".", ns: "EditorModal" })} onChange={setSrc} value={src} data-test-id="image-modal-url-input" showClear />
      <DialogActions>
        <Button
          appearance="primary"
          size="medium"
          isDisabled={isDisabled}
          callback={async () => {
            const rawSrc = await convertImageToBase64WithCanvas(src);

            const getPasteImageBlockData = async (file) => {
              const uploadedImageSrc = await uploadBase64Image({
                path: `image_upload/${currentDocumentKey}`,
                base64: file,
              });

              if (!uploadedImageSrc) {
                Sentry.withScope((scope) => {
                  scope.setLevel("error");
                  scope.setExtra("message", "s3 upload failed");
                  Sentry.captureMessage("SLID_WEB_ADD_IMAGE_BY_PASTE_ERROR");
                });
                return;
              }

              const clipRegisterResponse = await dispatch(
                registerClip({
                  imgSrc: uploadedImageSrc,
                  documentKey: currentDocumentKey,
                  clipTimestamp: null,
                  videoKey: null,
                  clipEndPoint: null,
                })
              );

              if (clipRegisterResponse.error_message) return;

              return {
                clipKey: clipRegisterResponse["clip_key"],
                src: uploadedImageSrc,
                documentKey: currentDocumentKey,
                type: "filePaste",
              };
            };

            const imageData = await getPasteImageBlockData(rawSrc);

            if (imageData) {
              onClick({
                src: imageData.src,
                sourceType: "linkPaste",
                clipKey: imageData.clipKey,
                originalImageSrc: imageData.src,
                isUploaded: true,
              });
            }
          }}
          text={t("image.createButton", { keySeparator: ".", ns: "EditorModal" })}
        />
      </DialogActions>
    </>
  );
}

export function InsertImageUploadedDialogBody({ onClick }: { onClick: (payload: InsertImagePayload) => void }) {
  const [src, setSrc] = useState("");
  const { isImageFileUploaded } = useImageNodeStore();
  const { t } = useTranslation("EditorModal");
  const isDisabled = src === "";
  const { currentDocumentKey } = useEditorStore();
  const dispatch = useAppDispatch();
  const loadImage = (files: FileList | null) => {
    const reader = new FileReader();
    reader.onload = function () {
      if (typeof reader.result === "string") {
        setSrc(reader.result);
      }
      return "";
    };
    if (files !== null) {
      reader.readAsDataURL(files[0]);
    }
  };

  return (
    <>
      <ImageFileInput label="Image Upload" onChange={loadImage} accept="image/*" data-test-id="image-modal-file-upload" />
      {isImageFileUploaded && (
        <DialogActions>
          <Button
            appearance="primary"
            size="medium"
            disabled={isDisabled}
            callback={async () => {
              const uploadedImageSrc = await uploadBase64Image({
                path: `image_upload/${currentDocumentKey}`,
                base64: src,
              });

              if (!uploadedImageSrc) {
                Sentry.withScope((scope) => {
                  scope.setLevel("error");
                  scope.setExtra("message", "s3 upload failed");
                  Sentry.captureMessage("SLID_WEB_ADD_IMAGE_BY_PASTE_ERROR");
                });
                return;
              }

              const clipRegisterResponse = await dispatch(
                registerClip({
                  imgSrc: uploadedImageSrc,
                  documentKey: currentDocumentKey,
                  clipTimestamp: null,
                  videoKey: null,
                  clipEndPoint: null,
                })
              );

              if (clipRegisterResponse.error_message) return;

              onClick({
                src: uploadedImageSrc,
                sourceType: "fileUpload",
                clipKey: clipRegisterResponse["clip_key"],
                originalImageSrc: uploadedImageSrc,
                isUploaded: true,
              });
            }}
            text={t("image.createButton", { keySeparator: ".", ns: "EditorModal" })}
          />
        </DialogActions>
      )}
    </>
  );
}

export function InsertImageDialog({ activeEditor, onClose }: { activeEditor: LexicalEditor; onClose: () => void }): JSX.Element {
  const { focusEditor } = useEditorStore();
  const [mode, setMode] = useState<null | "url" | "file">(null);
  const hasModifier = useRef(false);
  const { t } = useTranslation("EditorModal");

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [activeEditor]);

  const onClick = (payload: InsertImagePayload) => {
    focusEditor();
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    onClose();
  };

  // return (
  //   <>
  //     {!mode && (
  //       <DialogButtonsList>
  //         <Button appearance="line" callback={() => setMode("url")} text={t("image.url", { keySeparator: ".", ns: "EditorModal" })} size="medium" />
  //         <Button appearance="line" callback={() => setMode("file")} text={t("image.file", { keySeparator: ".", ns: "EditorModal" })} size="medium" />
  //       </DialogButtonsList>
  //     )}
  //     {mode === "url" && <InsertImageUriDialogBody onClick={onClick} />}
  //     {mode === "file" && <InsertImageUploadedDialogBody onClick={onClick} />}
  //   </>
  // );

  return <InsertImageUploadedDialogBody onClick={onClick} />;
}

export default function ImagesPlugin({ captionsEnabled }: { captionsEnabled?: boolean }): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([SlidImageNode])) {
      throw new Error("ImagesPlugin: ImageNode not registered on editor");
    }

    return mergeRegister(
      editor.registerCommand(
        FOCUS_IMAGE_DUMMY_TEXTAREA_COMMAND,
        () => {
          const imageNode = $getImageNodeInSelection();
          if (imageNode) {
            const imageNodeKey = imageNode.getKey();
            const imageNodeElement = editor.getElementByKey(imageNodeKey);
            if (imageNodeElement) {
              const input = imageNodeElement.querySelector("textarea");
              if (input) {
                input.focus();
              }
            }
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<InsertImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          const selection = $getSelection();
          const currentNode = selection?.getNodes()[0];
          const isParagraphNode = currentNode?.getType() === "slid-paragraph";
          const isEmptyParagraphNode = currentNode?.getTextContent() === "";
          if (isParagraphNode && isEmptyParagraphNode) {
            currentNode.replace(imageNode);
            const newParagraphNode = $createSlidParagraphNode();
            imageNode.insertAfter(newParagraphNode);
            newParagraphNode.select();
          } else {
            $insertNodeToNearestRoot(imageNode);
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [captionsEnabled, editor]);

  return null;
}

const TRANSPARENT_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const img = document.createElement("img");
img.src = TRANSPARENT_IMAGE;

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData("text/plain", "_");
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    "application/x-lexical-drag",
    JSON.stringify({
      data: {
        height: node.getData().height,
        key: node.getKey(),
        maxWidth: node.getData().maxWidth,
        src: node.getData().src,
        width: node.getData().width,
      },
      type: "image",
    })
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function $getImageNodeInSelection(): SlidImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertImagePayload {
  const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== "image") {
    return null;
  }

  return data;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target;
  return !!(target && target instanceof HTMLElement && !target.closest("code, span.editor-image") && target.parentElement && target.parentElement.closest("div.ContentEditable__root"));
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const target = event.target as null | Element | Document;
  const targetWindow = target == null ? null : target.nodeType === 9 ? (target as Document).defaultView : (target as Element).ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}
